/*
 * Copyright © 2015-2021 Serenova, LLC. All rights reserved.
 */

import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/catch';
import { fromPromise } from 'rxjs/observable/fromPromise';
import { fromJS, List } from 'immutable';

import { jsRuletoEdn, convertQueueQueryFromJsToEdnString } from './ednHelpers';
import { entitiesMetaData } from '../metaData';
import { sdkPromise } from '../../../../utils/sdk';
import { handleError, handleSuccess } from '../handleResult';

import { getCurrentFormValueByFieldName } from '../../form/selectors';
import { getUsers } from '../../../../redux/modules/entities/users/selectors';
import { getSkillsData } from '../../../../redux/modules/entities/skills/selectors';
import { getGroupsData } from '../../../../redux/modules/entities/groups/selectors';
import { getSelectedVersionBasicQuery, getVersionsCountInSelectedQueue } from './selectors';
import { getCurrentEntity, getSelectedEntity, getSelectedEntityId, getSelectedSubEntityId } from '../selectors';
import { getCurrentTenantDefaultSlaId } from '../slas/selectors';

export const InitQueuesSubEntityForm = (action$, store) =>
  action$
    .ofType('CREATE_QUEUE_VERSION_COPY')
    .map((a) => ({
      ...a,
      versionsCountInSelectedQueue: getVersionsCountInSelectedQueue(store.getState()),
    }))
    .map(({ version, versionsCountInSelectedQueue }) => {
      // Add empty list data to filters:
      const updatedBasicQuery =
        version &&
        version.basicQuery &&
        version.basicQuery.map((level) => {
          const updatedLevel =
            level['map'] &&
            level['map'].map((filters) => {
              const keyItem = filters.find((f) => f['key'] === 'after-seconds-in-queue');
              const sortItem = filters.find((f) => f['key'] === 'sort');
              if (!keyItem && !sortItem) {
                return filters.map((filter) => {
                  if (filter.key === 'query') {
                    return filter;
                  } else {
                    const updatedFilter =
                      filter['map'] &&
                      filter['map'].map &&
                      filter['map'].map((filterItem) => {
                        const entity = filterItem[0].key;
                        return filterItem.map((item) => {
                          if (item.key) {
                            return item;
                          } else {
                            const isFiltersEmpty = (someOrEvery) =>
                              item &&
                              item.find(
                                (listItem) =>
                                  listItem['list'] &&
                                  listItem['list'].find((list) => list.sym === someOrEvery) !== undefined
                              );
                            const newListItems = (someOrEvery) =>
                              entity === 'skills'
                                ? [{ list: [{ sym: someOrEvery }, { map: [] }] }]
                                : [{ list: [{ sym: someOrEvery }, { set: [] }] }];
                            if (!isFiltersEmpty('every') && (entity === 'skills' || entity === 'groups')) {
                              return newListItems('every').concat(item);
                            } else if (!isFiltersEmpty('some') && (entity === 'skills' || entity === 'groups')) {
                              return item.concat(newListItems('some'));
                            }
                            return item;
                          }
                        });
                      });
                    return { map: updatedFilter };
                  }
                });
              }
              return filters;
            });
          return { map: updatedLevel };
        });

      return {
        type: '@@redux-form/INITIALIZE',
        meta: {
          form: `queueQuery:${getSelectedSubEntityId(store.getState())}`,
        },
        payload: {
          name: `v${versionsCountInSelectedQueue + 1}`,
          queueId: version.queueId,
          query: version.query,
          queryVersion: version.queryVersion,
          basicQuery: updatedBasicQuery,
          slaId: version.slaId ? version.slaId : getCurrentTenantDefaultSlaId(store.getState()),
          minPriority: version.minPriority,
          maxPriority: version.maxPriority,
          priorityValue: version.priorityValue,
          priorityRate: version.priorityRate,
          priorityUnit: version.priorityUnit,
        },
      };
    });

export const AddFilter = (action$, store) =>
  action$
    .ofType('ADD_FILTER')
    .map((a) => ({
      ...a,
      selectedEntityId: getSelectedEntityId(store.getState()),
      selectedSubEntityId: getSelectedSubEntityId(store.getState()),
      basicQuery: getSelectedVersionBasicQuery(store.getState()),
    }))
    .map(({ index, entity, basicQuery, selectedEntityId, selectedSubEntityId }) => {
      const newSkillsList = [
        { key: entity },
        [{ list: [{ sym: 'every' }, { map: [] }] }, { list: [{ sym: 'some' }, { map: [] }] }],
      ];
      const newGroupsList = [
        { key: entity },
        [{ list: [{ sym: 'every' }, { set: [] }] }, { list: [{ sym: 'some' }, { set: [] }] }],
      ];
      const newUsersList = [{ key: 'user-id' }, [{ list: [{ sym: 'some' }, { set: [] }] }]];

      const getNewList = (entityType) => {
        if (entityType === 'skills') {
          return newSkillsList;
        } else if (entityType === 'groups') {
          return newGroupsList;
        } else if (entityType === 'users') {
          return newUsersList;
        }
      };
      const updatedBasicQuery =
        basicQuery &&
        basicQuery
          .updateIn([index, 'map', 1, 1, 'map'], (map) => map.push(fromJS(getNewList(entity))))
          .deleteIn([index, 'filterToAdd']);

      return {
        type: '@@redux-form/CHANGE',
        meta: {
          form: selectedEntityId === 'create' ? `queues:${selectedEntityId}` : `queueQuery:${selectedSubEntityId}`,
          field: 'basicQuery',
        },
        payload: updatedBasicQuery,
      };
    });

export const AddSearchQueryLevel = (action$, store) =>
  action$
    .ofType('ADD_SEARCH_QUERY_LEVEL')
    .map((a) => ({
      ...a,
      selectedEntityId: getSelectedEntityId(store.getState()),
      selectedSubEntityId: getSelectedSubEntityId(store.getState()),
      basicQuery: getSelectedVersionBasicQuery(store.getState()),
    }))
    .map(({ basicQuery, selectedEntityId, selectedSubEntityId }) => {
      const lastItemFromList = basicQuery && basicQuery.last();
      const lastItemInQueueCount =
        lastItemFromList &&
        lastItemFromList.get('map') &&
        lastItemFromList
          .get('map')
          .find((list) => list.find((item) => item.get('key') === 'after-seconds-in-queue'))
          .get(1);
      const lastItemInQueueFormat =
        lastItemFromList && lastItemFromList.getIn(['selectedFilters', 'inQueueTimeFormat']);
      const lastQuerySecondsInQueue =
        lastItemInQueueFormat === 'minutes' ? lastItemInQueueCount * 60 : lastItemInQueueCount;
      const newQueryLevelToAdd = {
        map: [
          [{ key: 'after-seconds-in-queue' }, lastQuerySecondsInQueue + 1],
          [{ key: 'query' }, { map: [] }],
          [{ key: 'sort' }, [{ list: [{ map: [] }] }]]
        ],
      };
      return {
        type: '@@redux-form/CHANGE',
        meta: {
          form: selectedEntityId === 'create' ? `queues:${selectedEntityId}` : `queueQuery:${selectedSubEntityId}`,
          field: 'basicQuery',
          touch: true,
        },
        payload: basicQuery.push(fromJS(newQueryLevelToAdd)),
      };
    });

export const AddFilterValue = (action$, store) =>
  action$
    .ofType('ADD_FILTER_ITEM')
    .map((a) => ({
      ...a,
      selectedEntityId: getSelectedEntityId(store.getState()),
      selectedSubEntityId: getSelectedSubEntityId(store.getState()),
      someOrEvery: a.allOrAny === 'All' ? 'every' : 'some',
      basicQuery: getSelectedVersionBasicQuery(store.getState()),
      dependentEntities: {
        skills: getSkillsData(store.getState()).toJS(),
        groups: getGroupsData(store.getState()).toJS(),
        'user-id': getUsers(store.getState()) && getUsers(store.getState()).toJS(),
      },
    }))
    .map(
      ({
        basicQuery,
        index,
        entity,
        allOrAny,
        someOrEvery,
        dependentEntities,
        selectedEntityId,
        selectedSubEntityId,
      }) => {
        const selectedFilters = basicQuery && basicQuery.getIn([index, 'selectedFilters', entity, allOrAny]);

        const selectedValues =
          selectedFilters &&
          selectedFilters
            .update((values) => {
              //trimming blank spaces from entityItem name
              const selectedEntity = dependentEntities[entity].find(
                (entityItem) =>
                  entity === 'user-id'
                    ? `${entityItem.firstName} ${entityItem.lastName}` === values.get('val')
                    : entityItem.name.replace(/\s+/g, ' ').trim() === values.get('val').replace(/\s+/g, ' ').trim()
              );
              return selectedEntity ? values.set('val', selectedEntity.id) : values;
            })
            .toJS();

        // Adds the selected entity (skill, user, group) item to the existing list:
        const updatedFilterItem = (listItem) => {
          const listToUpdate = listItem.getIn(['list', 0, 'sym']) === someOrEvery;
          if (listToUpdate) {
            return entity === 'skills'
              ? listItem.updateIn(['list', 1, 'map'], (map) =>
                  map.push(
                    fromJS([
                      { tag: 'uuid', val: selectedValues.val },
                      {
                        list: [
                          {
                            sym: selectedValues.proficiencyType ? selectedValues.proficiencyType : '>=',
                          },
                          selectedValues.proficiencyCount ? parseInt(selectedValues.proficiencyCount, 10) : 1,
                        ],
                      },
                    ])
                  )
                )
              : listItem.updateIn(['list', 1, 'set'], (set) =>
                  set.push(fromJS({ tag: 'uuid', val: selectedValues.val }))
                );
          }
          return listItem;
        };

        const updatedBasicQuery =
          basicQuery &&
          basicQuery
            .updateIn([index, 'map', 1, 1, 'map'], (filters) => {
              const filterItems =
                filters &&
                filters.map((filter) => {
                  const keyItem = filter.find((f) => f.get('key'));
                  if (keyItem && keyItem.get('key') === entity) {
                    return filter.map((filterItem) => {
                      if (!filterItem.get('key')) {
                        return filterItem.map(updatedFilterItem);
                      }
                      return filterItem;
                    });
                  }
                  return filter;
                });
              return filterItems;
            })
            .deleteIn([index, 'selectedFilters', entity, allOrAny]);

        return {
          type: '@@redux-form/CHANGE',
          meta: {
            form: selectedEntityId === 'create' ? `queues:${selectedEntityId}` : `queueQuery:${selectedSubEntityId}`,
            field: 'basicQuery',
            touch: true,
          },
          payload: updatedBasicQuery,
        };
      }
    );

export const AddSortByValue = (action$, store) =>
  action$
    .ofType('ADD_SORT_ITEM')
    .map((a) => ({
      ...a,
      selectedEntityId: getSelectedEntityId(store.getState()),
      selectedSubEntityId: getSelectedSubEntityId(store.getState()),
      basicQuery: getSelectedVersionBasicQuery(store.getState()),
      dependentEntities: {
        skills: getSkillsData(store.getState()).toJS(),
      },
    }))
    .map(
      ({
         basicQuery,
         index,
         entity,
         dependentEntities,
         selectedEntityId,
         selectedSubEntityId,
       }) => {
        const selectedFilters = basicQuery && basicQuery.getIn([index, 'sortBy', entity, 'sortBy', 'val']);
        const sortByOrder = basicQuery && basicQuery.getIn([index, 'sortBy', entity, 'order', 'val']);
        //trimming blank spaces from entityItem name
        const selectedEntity = dependentEntities[entity].find(
          (entityItem) => entityItem.name.replace(/\s+/g, ' ').trim() === selectedFilters.replace(/\s+/g, ' ').trim()
        );
        const selectedValues = selectedEntity && selectedEntity.id;

        // Adds the selected item to the existing list:

        if (basicQuery.getIn([index, 'map', 2]) == undefined) {
          basicQuery = basicQuery.updateIn([index, 'map'], (item) => {
            return item.push(fromJS([{ key: 'sort' }, [{ list: [{ map: [] }] }]]))
          });
        }
        const updatedBasicQuery =
          basicQuery &&
          basicQuery.updateIn([index, 'map', 2, 1, 0, 'list', 0, 'map'], (filters) => {
            return filters.push(
              fromJS(
                    [
                      { tag: 'uuid', val: selectedValues },
                      { key: sortByOrder },
                    ]
              ));
          })
            .deleteIn([index, 'sortBy', entity]);

        return {
          type: '@@redux-form/CHANGE',
          meta: {
            form: selectedEntityId === 'create' ? `queues:${selectedEntityId}` : `queueQuery:${selectedSubEntityId}`,
            field: 'basicQuery',
            touch: true,
          },
          payload: updatedBasicQuery,
        };
      }
    );

export const RemoveQueueQueryFilter = (action$, store) =>
  action$
    .ofType('REMOVE_QUEUE_QUERY_FILTER')
    .map((a) => ({
      ...a,
      selectedEntityId: getSelectedEntityId(store.getState()),
      selectedSubEntityId: getSelectedSubEntityId(store.getState()),
      basicQuery: getSelectedVersionBasicQuery(store.getState()),
    }))
    .map(({ index, entity, basicQuery, selectedEntityId, selectedSubEntityId }) => {
      let updatedBasicQuery =
        basicQuery &&
        basicQuery.updateIn([index, 'map', 1, 1, 'map'], (filters) => {
          const filterIndexToRemove = filters.findIndex((filter) => filter.find((f) => f.get('key') === entity));
          return filters.delete(filterIndexToRemove);
        });
      return {
        type: '@@redux-form/CHANGE',
        meta: {
          form: selectedEntityId === 'create' ? `queues:${selectedEntityId}` : `queueQuery:${selectedSubEntityId}`,
          field: 'basicQuery',
        },
        payload: updatedBasicQuery,
      };
    });

export const RemoveFilterItem = (action$, store) =>
  action$
    .ofType('REMOVE_FILTER_ITEM')
    .map((a) => ({
      ...a,
      selectedEntityId: getSelectedEntityId(store.getState()),
      selectedSubEntityId: getSelectedSubEntityId(store.getState()),
      someOrEvery: a.allOrAny === 'All' ? 'every' : 'some',
      basicQuery: getSelectedVersionBasicQuery(store.getState()),
    }))
    .map(({ index, entity, someOrEvery, removeItemId, basicQuery, selectedEntityId, selectedSubEntityId }) => {
      const updateFilterItems = (listItems) => {
        const listToUpdate = listItems.getIn(['list', 0, 'sym']) === someOrEvery;
        if (listToUpdate) {
          if (entity === 'skills') {
            return listItems.updateIn(['list', 1, 'map'], (listItem) =>
              listItem.filter((item) => item.getIn([0, 'tag']) === 'uuid' && item.getIn([0, 'val']) !== removeItemId)
            );
          } else {
            return listItems.updateIn(['list', 1, 'set'], (listItem) =>
              listItem.filter((item) => item.get('val') !== removeItemId)
            );
          }
        }
        return listItems;
      };

      const updatedBasicQuery =
        basicQuery &&
        basicQuery.updateIn([index, 'map', 1, 1, 'map'], (filters) => {
          const filterItems =
            filters &&
            filters.map((filter) => {
              const keyItem = filter.find((f) => f.get('key'));
              if (keyItem && keyItem.get('key') === entity) {
                return filter.map((filterItem) => {
                  if (!filterItem.get('key')) {
                    return filterItem.map(updateFilterItems);
                  }
                  return filterItem;
                });
              }
              return filter;
            });
          return filterItems;
        });

      return {
        type: '@@redux-form/CHANGE',
        meta: {
          form: selectedEntityId === 'create' ? `queues:${selectedEntityId}` : `queueQuery:${selectedSubEntityId}`,
          field: 'basicQuery',
          touch: true,
        },
        payload: updatedBasicQuery,
      };
    });

export const RemoveSortItem = (action$, store) =>
  action$
    .ofType('REMOVE_SORT_ITEM', 'REMOVE_FILTER_ITEM')
    .map((a) => ({
      ...a,
      selectedEntityId: getSelectedEntityId(store.getState()),
      selectedSubEntityId: getSelectedSubEntityId(store.getState()),
      basicQuery: getSelectedVersionBasicQuery(store.getState()),
    }))
    .map(({ index, removeItemId, basicQuery, selectedEntityId, selectedSubEntityId }) => {
      const updatedBasicQuery =
        basicQuery &&
        basicQuery.updateIn([index, 'map', 2, 1, 0, 'list', 0, 'map'], (filters) => {
          const filterItems =
            filters &&
            filters.update((filter) => {
              return filter.filter((listItem) =>
                listItem.getIn([0, 'tag']) === 'uuid' && listItem.getIn([0, 'val']) !== removeItemId
              );
            });
          return filterItems;
        });

      return {
        type: '@@redux-form/CHANGE',
        meta: {
          form: selectedEntityId === 'create' ? `queues:${selectedEntityId}` : `queueQuery:${selectedSubEntityId}`,
          field: 'basicQuery',
          touch: true,
        },
        payload: updatedBasicQuery,
      };
    });

export const ConvertQueueQueryToEdn = (action$, store) =>
  action$
    .ofType('CONVERT_QUEUE_QUERY_TO_EDN')
    .map((action) => ({
      ...action,
      entityId: getSelectedEntityId(store.getState()),
      entityName: getCurrentEntity(store.getState()),
      selectedEntityId: getSelectedEntityId(store.getState()),
      selectedSubEntityId: getSelectedSubEntityId(store.getState()),
    }))
    .filter(({ entityName }) => entityName === 'queues')
    .map((a) => {
      // update query level time format to seconds if selected time format is in 'minutes'
      const updatedBasicQuery =
        a.basicQuery &&
        a.basicQuery.map((level) => {
          return level.getIn(['selectedFilters', 'inQueueTimeFormat']) === 'minutes'
            ? level.updateIn(['map', 0, 1], (secondsInQueue) => secondsInQueue * 60)
            : level;
        });

      const modifiedBasicQuery = updatedBasicQuery.map((level) =>
        level.updateIn(['map', 1, 1, 'map'], (filters) =>
          filters.reduce((accumlator, lists) => {
            const filterKey = lists && lists.get(0);
            const updatedLists =
              lists &&
              lists.get(1) &&
              lists
                .get(1)
                .reduce((acc, list) => {
                  const updatedListItem =
                    list &&
                    list.filter((listItem) => {
                      return filterKey.get('key') === 'skills'
                        ? listItem.getIn([1, 'map']).size !== 0
                        : listItem.getIn([1, 'set']).size !== 0;
                    });
                  return acc.push(updatedListItem);
                }, List([]))
                .filter((a) => a.size !== 0);
            return updatedLists.size !== 0 ? accumlator.push(List([filterKey, updatedLists])) : accumlator;
          }, List([]))
        )
      );
      // parse JS object to edn string
      const parsedQuery = jsRuletoEdn(modifiedBasicQuery.toJS());

      return {
        type: '@@redux-form/CHANGE',
        meta: {
          form:
            a.selectedEntityId === 'create' ? `queues:${a.selectedEntityId}` : `queueQuery:${a.selectedSubEntityId}`,
          field: 'query',
          touch: true,
        },
        payload: parsedQuery,
      };
    });

export const ConvertEdnStringToImmutable = (action$, store) =>
  action$
    .ofType('CONVERT_EDN_STRING_TO_IMMUTABLE')
    .map((a) => ({
      ...a,
      selectedEntityId: getSelectedEntityId(store.getState()),
      selectedSubEntityId: getSelectedSubEntityId(store.getState()),
      query: getCurrentFormValueByFieldName(store.getState(), 'query'),
    }))
    .map(({ selectedEntityId, selectedSubEntityId, query }) => {
      // const parsedQuery = ednRuleToImmutable(query);
      return { type: 'CONVERTED' };
      // TODO: Convert edn to JS object if parsable or shown error 'cannot parse edn'
      /*
      return {
        type: '@@redux-form/CHANGE',
        meta: {
          form:
            selectedEntityId === 'create' ? `queues:${selectedEntityId}` : `queueQuery:${selectedSubEntityId}`,
          field: 'basicQuery',
          touch: true,
        },
        payload: parsedQuery,
      };
      */
    });

export const QueuesFormSubmission = (action$, store) =>
  action$
    .ofType('FORM_SUBMIT')
    .map((action) => ({
      ...action,
      entityId: getSelectedEntityId(store.getState()),
      entityName: getCurrentEntity(store.getState()),
    }))
    .filter(({ entityName }) => entityName === 'queues')
    .map((a) => {
      if (a.entityId === 'create') {
        return {
          type: 'CREATE_ENTITY',
          entityName: a.entityName,
          entityId: a.entityId,
          values: convertQueueQueryFromJsToEdnString(a.values),
        };
      } else if (a.entityId === 'bulk') {
        return {
          type: 'BULK_ENTITY_UPDATE',
          entityName: a.entityName,
          values: a.values.toJS(),
        };
      } else {
        return {
          type: 'UPDATE_ENTITY',
          entityId: a.entityId,
          entityName: a.entityName,
          values: {
            name: a.values.get('name'),
            id: a.entityId,
            description: !a.values.get('description') ? '' : a.values.get('description'),
            activeVersion: a.values.get('activeVersion'),
          },
        };
      }
    });

export const CreateQueue = (action$) =>
  action$
    .ofType('CREATE_ENTITY')
    .filter(({ entityName }) => entityName === 'queues')
    .concatMap((a) => {
      a.sdkCall = entitiesMetaData[a.entityName].entityApiRequest('create', 'singleMainEntity');
      a.sdkCall.path = entitiesMetaData[a.entityName].sdkCall.path;
      a.sdkCall.data = {
        name: a.values.name,
        description: !a.values.description ? '' : a.values.description,
      };
      return fromPromise(sdkPromise(a.sdkCall))
        .map((response) => handleSuccess(response, a, 'Queue was created successfully!'))
        .catch((error) => handleError(error, a));
    });

export const CreateQueueVersionAfterCreatingNewQueue = (action$, store) =>
  action$
    .ofType('CREATE_ENTITY_FULFILLED')
    .filter(({ entityName }) => entityName === 'queues')
    .map((a) => ({
      ...a,
      selectedEntity: getSelectedEntity(store.getState()),
      selectedEntityId: getSelectedEntityId(store.getState()),
    }))
    .concatMap((a) => {
      const { result } = a.response;
      a.sdkCall = entitiesMetaData['queues'].entityApiRequest('create', 'singleMainEntity');
      a.sdkCall.path = ['queues', result.id, 'versions'];
      delete a.values.description;
      delete a.values.activeVersion;
      if (a.values.query.indexOf(':sort') > 0) {
        a.values = {
          ...a.values,
          sortByProficiency: true
        }
      }

      a.sdkCall.data = {
        ...a.values,
        queryVersion: 2,
        name: 'v1',
      };
      return fromPromise(sdkPromise(a.sdkCall))
        .map((response) =>
          handleSuccess(response, { ...a, type: 'CREATE_QUEUE_VERSION' }, `Queue version created successfully!`)
        )
        .catch((error) =>
          handleError(
            error,
            { ...a, queueId: result.id, type: 'CREATE_QUEUE_VERSION' },
            'Unable to create queue version'
          )
        );
    });

export const ActivateQueue = (action$) =>
  action$
    .ofType('CREATE_QUEUE_VERSION_FULFILLED')
    .filter(({ entityName }) => entityName === 'queues')
    .concatMap((a) => {
      const { result } = a.response;
      a.sdkCall = entitiesMetaData['queues'].entityApiRequest('update', 'singleMainEntity');
      a.sdkCall.path = ['queues', result.queueId];
      a.sdkCall.data = {
        name: a.values.name,
        activeVersion: result.version,
        active: true,
      };
      return fromPromise(sdkPromise(a.sdkCall))
        .map((response) =>
          handleSuccess(
            response,
            { ...a, queueId: result.queueId, type: 'ACTIVATE_QUEUE' },
            `Queue was set to active successfully!`
          )
        )
        .catch((error) =>
          handleError(error, { ...a, queueId: result.queueId, type: 'ACTIVATE_QUEUE' }, 'Unable to set Queue to active')
        );
    });

export const FetchCreatedQueueInfo = (action$) =>
  action$
    .ofType('CREATE_QUEUE_VERSION_REJECTED', 'ACTIVATE_QUEUE_FULFILLED', 'ACTIVATE_QUEUE_REJECTED')
    .filter(({ entityName }) => entityName === 'queues')
    .map(({ queueId }) => ({
      type: 'FETCH_DATA_ITEM',
      entityName: 'queues',
      id: queueId,
      createdNewQueue: true,
    }));

export const QueueQueryFormSubmission = (action$, store) =>
  action$
    .ofType('SUB_ENTITY_FORM_SUBMIT')
    .map((action) => ({
      ...action,
      entityId: getSelectedEntityId(store.getState()),
      entityName: getCurrentEntity(store.getState()),
      selectedSubEntityId: getSelectedSubEntityId(store.getState()),
      versionCountInSelectedQueue: getVersionsCountInSelectedQueue(store.getState()),
    }))
    .filter(({ entityName }) => entityName === 'queues')
    .map((a) => ({
      type: 'CREATE_SUB_ENTITY',
      entityName: a.entityName,
      entityId: a.entityId,
      selectedSubEntityId: a.selectedSubEntityId,
      values: {
        ...convertQueueQueryFromJsToEdnString(a.values),
        queryVersion: 2, // always set queryVersion to 2 while creating a new queue version
        name: `v${a.versionCountInSelectedQueue === 0 ? 1 : a.versionCountInSelectedQueue + 1}`,
      },
    }));

export const CreateQueueVersion = (action$, store) =>
  action$
    .ofType('CREATE_SUB_ENTITY')
    .filter(({ entityName }) => entityName === 'queues')
    .map((a) => {
      if (a.values.query.indexOf(':sort') > 0) {
        a.values = {
          ...a.values,
          sortByProficiency: true
        }
      }

      a.sdkCall = entitiesMetaData[a.entityName].entityApiRequest('create', 'singleMainEntity');
      a.sdkCall.path = ['queues', getSelectedEntityId(store.getState()), 'versions'];
      a.sdkCall.data = a.values;
      return { ...a };
    })
    .concatMap((a) =>
      fromPromise(sdkPromise(a.sdkCall))
        .map((response) => handleSuccess(response, a, `Queue Version created successfully!`))
        .catch((error) => handleError(error, a))
    );

export const ReInitQueuesFormAfterCreatingNewVersion = (action$) =>
  action$
    .ofType('CREATE_SUB_ENTITY_FULFILLED')
    .filter(({ entityName }) => entityName === 'queues')
    .map(({ entityName, response }) => ({
      type: 'FETCH_DATA_ITEM',
      entityName,
      id: response.result.queueId,
    }));

// flag used to show/hide "sort by skills proficiency" feature
export const FetchPriorityBaseRouting = (action$, store) =>
  action$
    .ofType('FETCH_DATA_FULFILLED')
    .map((a) => {
      return ({
        ...a,
        entityName: getCurrentEntity(store.getState()),
        selectedEntity: getSelectedEntity(store.getState()),
      })
    })
    .filter(
      ({ entityName }) => entityName === 'queues'
    )
    .concatMap((a) =>
      fromPromise(
        sdkPromise({
          crudAction: 'read',
          path: ['settings'],
          command: 'getMatchmaker2Enabled',
          module: 'entities',
          topic: 'cxengage/entities/get-tenant-settings/matchmaker2-enabled',
        })
      )
        .map((response) => handleSuccess(response, { ...a, type: 'FETCH_PRIORITY_BASE_ROUTING' }))
        .catch((error) => handleError(error, {
          ...a,
          type: 'FETCH_PRIORITY_BASE_ROUTING'
        }, 'Unable to fetch priority based routing flag'))
    );
